import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Search,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useQuery, useQueryClient } from 'react-query';
import { supabase, Subscription } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { getCategoryColor, CATEGORIES } from '../utils/categoryColors';
import EditSubscriptionModal from '../components/EditSubscriptionModal';
import toast from 'react-hot-toast';
import ServiceLogo from '../components/ServiceLogo';
import { formatBillingCycleForDisplay } from '../utils/billingCycleUtils';

type SortField = 'name' | 'cost' | 'next_renewal_date' | 'category';
type SortOrder = 'asc' | 'desc';

const AllSubscriptions: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // State for filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<Subscription | null>(null);

  const { data: subscriptions, isLoading } = useQuery(
    ['subscriptions', user?.id],
    async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('next_renewal_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    {
      enabled: !!user,
    }
  );

  // Filter and sort subscriptions
  const filteredAndSortedSubscriptions = subscriptions
    ?.filter(sub => {
      const matchesSearch = sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          sub.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || sub.category === categoryFilter;
      const matchesStatus = statusFilter === 'all' || 
                          (statusFilter === 'active' && sub.is_active) ||
                          (statusFilter === 'inactive' && !sub.is_active);
      
      return matchesSearch && matchesCategory && matchesStatus;
    })
    .sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      if (sortField === 'cost') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (sortField === 'next_renewal_date') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = aValue?.toLowerCase() || '';
        bValue = bValue?.toLowerCase() || '';
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    }) || [];

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleEditSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedSubscription(null);
  };

  const handleDeleteSubscription = (subscription: Subscription) => {
    setSubscriptionToDelete(subscription);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!subscriptionToDelete) return;
    
    try {
      const { error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('id', subscriptionToDelete.id);
      
      if (error) throw error;
      
      queryClient.invalidateQueries(['subscriptions', user?.id]);
      toast.success('Subscription deleted successfully!');
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast.error('Failed to delete subscription');
    } finally {
      setDeleteDialogOpen(false);
      setSubscriptionToDelete(null);
    }
  };

  const handleToggleSubscription = async (subscription: Subscription) => {
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({ is_active: !subscription.is_active })
        .eq('id', subscription.id);
      
      if (error) throw error;
      
      queryClient.invalidateQueries(['subscriptions', user?.id]);
      toast.success(`Subscription ${subscription.is_active ? 'disabled' : 'enabled'} successfully!`);
    } catch (error) {
      console.error('Error toggling subscription:', error);
      toast.error('Failed to update subscription status');
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress 
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C00 50%, #32CD32 100%)',
            },
          }}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          All Subscriptions
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage all your subscriptions in one place.
        </Typography>
      </Box>

      {/* Filters and Actions */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search subscriptions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => navigate('/add-subscription')}
                fullWidth
              >
                Add New
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary">
              {filteredAndSortedSubscriptions.length} of {subscriptions?.length || 0} subscriptions
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Subscriptions Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'cost'}
                  direction={sortField === 'cost' ? sortOrder : 'asc'}
                  onClick={() => handleSort('cost')}
                >
                  Cost
                </TableSortLabel>
              </TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Billing Cycle</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'next_renewal_date'}
                  direction={sortField === 'next_renewal_date' ? sortOrder : 'asc'}
                  onClick={() => handleSort('next_renewal_date')}
                >
                  Next Payment
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedSubscriptions.map((subscription) => (
              <TableRow
                key={subscription.id}
                sx={{
                  opacity: subscription.is_active ? 1 : 0.6,
                  backgroundColor: subscription.is_active ? 'inherit' : 'rgba(0,0,0,0.02)',
                }}
              >
                <TableCell>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <ServiceLogo
                        serviceName={subscription.name}
                        category={subscription.category}
                        size="small"
                      />
                      <Typography variant="body1" fontWeight="medium">
                        {subscription.name}
                      </Typography>
                    </Box>
                    {subscription.notes && (
                      <Typography variant="body2" color="text.secondary">
                        {subscription.notes}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(subscription.cost, subscription.currency)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={subscription.category}
                    size="small"
                    sx={{
                      backgroundColor: getCategoryColor(subscription.category),
                      color: 'white',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatBillingCycleForDisplay(subscription.billing_cycle)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {format(new Date(subscription.next_renewal_date), 'MMM dd, yyyy')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={subscription.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={subscription.is_active ? 'success' : 'default'}
                    variant={subscription.is_active ? 'filled' : 'outlined'}
                    sx={{ 
                      color: subscription.is_active ? 'white' : 'inherit',
                      fontWeight: 500
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleSubscription(subscription)}
                      title={subscription.is_active ? 'Disable' : 'Enable'}
                    >
                      {subscription.is_active ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditSubscription(subscription)}
                      title="Edit"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteSubscription(subscription)}
                      title="Delete"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Modal */}
      <EditSubscriptionModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        subscription={selectedSubscription}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Subscription</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{subscriptionToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AllSubscriptions; 